/* eslint-disable no-shadow */
import {
  Store, GetterTree, MutationTree, ActionTree, Module
} from 'vuex';
import InitialSettingsViewModel from '@/models/InitialSettingsViewModel';
import Policy from '@/models/Policy';
// eslint-disable-next-line import/no-cycle
import { policyService } from '@/services/PolicyService';
import OptInData from '@/models/OptInData';
import IPolicyState from '../states/interfaces/policy-state.d';
import PolicyState from '../states/policy-state';
import IRootState from '../states/interfaces/index.d';

export const state: IPolicyState = new PolicyState();

export const getters: GetterTree<IPolicyState, IRootState> = {
  privacyPolicyVersionId: (state) => state.privacyPolicyVersionId,
  termsPolicyVersionId: (state) => state.termsPolicyVersionId,
  marketingPolicyVersionId: (state) => state.marketingPolicyVersionId,
  partnerMarketingPolicyVersionId: (state) => state.partnerMarketingPolicyVersionId,
  fCAPolicyVersionId: (state) => state.fCAPolicyVersionId,
  servicePolicy: (state) => state.servicePolicy,
  defaultEmailMicroCopy: (state) => state.defaultEmailMicroCopy,
  defaultPhoneMicroCopy: (state) => state.defaultPhoneMicroCopy
};

export const mutations: MutationTree<IPolicyState> = {
  setDefaultEmailMicroCopyText: (state: PolicyState, microCopyText: string) => {
    state.defaultEmailMicroCopy = microCopyText;
  },
  setDefaultPhoneMicroCopyText: (state: PolicyState, microCopyText: string) => {
    state.defaultPhoneMicroCopy = microCopyText;
  },
  setServicePolicy: (state: PolicyState, policy: Policy) => {
    state.servicePolicy = policy;
  },
  setMarketingPolicyId: (state: PolicyState, marketingPolicyId: string) => {
    state.marketingPolicyVersionId = marketingPolicyId;
  },
  setOptInText: (state: PolicyState, optInText: string) => {
    state.optInText = optInText;
  },
  setPartnerPolicyId: (state: PolicyState, partnerPolicyId: string) => {
    state.partnerMarketingPolicyVersionId = partnerPolicyId;
  },
  setPartnerPolicyText: (state: PolicyState, partnerPolicyText: string) => {
    state.partnerPolicyText = partnerPolicyText;
  },
  setFcaPolicyId: (state: PolicyState, facPolicyId: string) => {
    state.fCAPolicyVersionId = facPolicyId;
  },
  setPrivacyPolicyId: (state: PolicyState, privacyPolicyId: string) => {
    state.privacyPolicyVersionId = privacyPolicyId;
  },
  setTermsPolicyId: (state: PolicyState, termsPolicyId: string) => {
    state.termsPolicyVersionId = termsPolicyId;
  }
};

export const actions: ActionTree<IPolicyState, IRootState> = {
  submitDefaultEmailMicroCopyText: ({ commit }, microCopyText: string) => {
    commit('setDefaultEmailMicroCopyText', microCopyText);
  },
  submitDefaultPhoneMicroCopyText: ({ commit }, microCopyText: string) => {
    commit('setDefaultPhoneMicroCopyText', microCopyText);
  },
  submitPolicyId: async ({ dispatch, rootState }) => {
    let policyId = '';

    const peugeotId = 39;
    const citroenId = 10;
    const peugeotVanId = 126;
    const citroenVanId = 105;
    const mgId = 89;
    const audi = 4;
    const skoda = 49;
    const nissanId = 37;

    const manufacturerId = rootState.searchQueryModule.searchQuery.manufacturerIds![0];

    if (policyService.isAskAPrice) {
      if (manufacturerId === citroenId || manufacturerId === citroenVanId) {
        policyId = 'e6413682-4ff5-4d40-b3e0-97f30e43ddbc';
      } else if (manufacturerId === peugeotId || manufacturerId === peugeotVanId) {
        policyId = '004d4f15-0014-4cd9-859c-3ec77b27dcb6';
      } else if (manufacturerId === mgId) {
        policyId = 'a911084e-53bf-4fe2-b4b5-2e16bbe642d0';
      } else if (manufacturerId === audi) {
        policyId = 'a50f6cdc-0ea4-4bcf-ac01-cf2d31c35f03';
      } else if (manufacturerId === skoda) {
        policyId = '66bcd315-4348-4b61-a7ac-025e017e16b7';
      } else if (manufacturerId === nissanId) {
        policyId = 'dc5a00d4-9d0f-4acd-8b2c-c8ef8e2a9f5a';
      }
    } else if (policyService.isCarKeys) {
      if (manufacturerId === citroenId || manufacturerId === citroenVanId) {
        policyId = '0027efe0-2ac4-475f-b723-b7beb84d4284';
      } else if (manufacturerId === peugeotId || manufacturerId === peugeotVanId) {
        policyId = '5639d512-ea82-4076-a7fc-1156860dcfcb';
      } else if (manufacturerId === mgId) {
        policyId = 'a104053e-3ea1-4b53-b5c9-076fc8e5d2bc';
      } else if (manufacturerId === nissanId) {
        policyId = '0c1f5755-44f8-4e5f-9fa4-c90835c60545';
      }
    } else if (policyService.isGreenCar) {
      if (manufacturerId === nissanId) {
        policyId = '27225d9f-9e87-4c57-abb5-c43fe348ea7d';
      }
    }

    if (policyId !== '') {
      dispatch('submitServicePolicy', await policyService.getPolicy(policyId));
    } else {
      const policy = new Policy();
      policy.policyVersionId = rootState.initialSettingsModule.initialSettings!.servicePolicyVersionId;
      policy.policySummary = rootState.initialSettingsModule.initialSettings!.servicePolicyVersionText as string;
      policy.policyId = '';

      dispatch('submitServicePolicy', policy);
    }
  },
  submitServicePolicy({ commit }, policy: Policy) {
    commit('setServicePolicy', policy);
  },
  submitPolicyData: ({ commit, dispatch }, initialSettings: InitialSettingsViewModel) => {
    const policy = new Policy();
    policy.policyVersionId = initialSettings.servicePolicyVersionId;
    policy.policySummary = initialSettings.servicePolicyVersionText;
    commit('setServicePolicy', policy);
    commit('setMarketingPolicyId', initialSettings.marketingPolicyVersionId);
    commit('setOptInText', initialSettings.marketingPolicyVersionText);
    commit('setPartnerPolicyId', initialSettings.partnerMarketingPolicyVersionId);
    commit('setPartnerPolicyText', initialSettings.partnerMarketingPolicyVersionText);
    commit('setFcaPolicyId', initialSettings.fcaPolicyVersionId);
    commit('setPrivacyPolicyId', initialSettings.privacyPolicyVersionId);
    commit('setTermsPolicyId', initialSettings.termsPolicyVersionId);
    dispatch('submitDefaultMarketingOptIn', initialSettings);
  },
  submitDefaultMarketingOptIn: ({ dispatch }, initialSettings: InitialSettingsViewModel) => {
    const optInData = new OptInData();
    optInData.PolicyType = 1;
    optInData.PublishedPolicyId = initialSettings.marketingPolicyVersionId;
    dispatch('enquiryModule/submitOptInConsent', optInData, { root: true });
  }
};

export const policyModule: Module<IPolicyState, IRootState> = {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
